import { Store } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { createManagementActions } from './createManagementStore';

export type PublisherKybStatusFilter =
  | 'all'
  | 'NotStarted'
  | 'InProgress'
  | 'UnderReview'
  | 'Approved'
  | 'Rejected'
  | 'RequiresAdditionalInfo';

export type PublisherTypeFilter = 'all' | 'Organization' | 'Individual';

export type PublisherDappsFilter = 'all' | 'true' | 'false';

export interface PublisherDetail {
  id: string;
  type: 'Organization' | 'Individual';
  name: string;
  website: string;
  address: string | null;
  avatarUrl: string | null;
  avatarThumbnailUrl: string | null;
  walletAddress: string | null;
  nftMintAddress: string | null;
  kybStatus:
    | 'NotStarted'
    | 'InProgress'
    | 'UnderReview'
    | 'Approved'
    | 'Rejected'
    | 'RequiresAdditionalInfo'
    | null;
  isOwner: boolean;
  isFrozen: boolean;
  kycKybBypassEnabled: boolean;
  memberCount: number;
  dappCount: number;
  publishedDappCount: number;
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
  };
  organizationId: string | null;
  individualId: string | null;
  created: number;
  updated: number | null;
}

export interface PublishersPageState {
  publishers: PublisherDetail[];
  filters: {
    type: PublisherTypeFilter;
    kybStatus: PublisherKybStatusFilter;
    hasDapps: PublisherDappsFilter;
    ownersOnly: boolean;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  ui: {
    isLoading: boolean;
  };
}

const initialFilters: PublishersPageState['filters'] = {
  type: 'all',
  kybStatus: 'all',
  hasDapps: 'all',
  ownersOnly: false,
  search: '',
};

const defaultSorting: SortingState = [{ id: 'created', desc: true }];

export const publishersPageStore = new Store<PublishersPageState>({
  publishers: [],
  filters: initialFilters,
  sorting: defaultSorting,
  pagination: { currentPage: 1, totalPages: 1, total: 0 },
  ui: { isLoading: true },
});

const actions = createManagementActions(publishersPageStore, {
  initialFilters,
  defaultSorting,
});

export const {
  updateFilter,
  setSorting,
  setPage,
  setPaginationInfo,
  setLoading,
  resetFilters,
} = actions;

export const setPublishersQueryState = actions.setQueryState;

export const setPublishers = (publishers: PublisherDetail[]) => {
  publishersPageStore.setState((prev) => ({ ...prev, publishers }));
};

export const updatePublisherKycKybBypassState = (
  publisher: Pick<PublisherDetail, 'organizationId' | 'individualId' | 'id'>,
  kycKybBypassEnabled: boolean,
) => {
  publishersPageStore.setState((prev) => ({
    ...prev,
    publishers: prev.publishers.map((item) => {
      const isSamePublisher = Boolean(
        (publisher.organizationId &&
          item.organizationId === publisher.organizationId) ||
        (publisher.individualId &&
          item.individualId === publisher.individualId) ||
        item.id === publisher.id,
      );

      return isSamePublisher ? { ...item, kycKybBypassEnabled } : item;
    }),
  }));
};
